import { useMemo } from "react";
import useCustomFilters, {
  FilterOption,
} from "components/custom-filters-list/filters/use-custom-filters.hook";
import { SalesFilters } from "services/types/sales.type";
import SelectClinic from "components/custom-filters-list/filters/select-clinic.component";
import SelectAgingGroup from "components/custom-filters-list/filters/select-aging-group.component";
import SelectPatient from "components/custom-filters-list/filters/select-patient.component";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import SelectPaymentSourceType from "components/custom-filters-list/filters/select-payment-source-type.component";
import SelectTag from "components/custom-filters-list/filters/select-tag.component";
import { TagTypes } from "constants/tags";
import SelectBooleanValue from "components/custom-filters-list/filters/select-boolean-value.component";
import { ReportFilter } from "features/reports/types";
import SelectRegion from "components/custom-filters-list/filters/select-region.component";
import * as React from "react";
import { useAppSelector } from "app/hooks";
import { selectHasRegions } from "features/auth/auth-slice";
import SelectInsurer from "components/custom-filters-list/filters/select-insurer.component";
import SelectManagedCarePlan from "components/custom-filters-list/filters/select-managed-care-plan.component";
import { Insurer } from "services/types/insurers.type";

export type AccountReceivableCustomFiltersValues = {
  clinic?: ReportFilter<string[]>;
  region?: ReportFilter<string[]>;
  sale_age?: ReportFilter<string[]>;
  patient?: ReportFilter<string>;
  provider?: ReportFilter<string[]>;
  payment_source_type: ReportFilter<string>;
  assigned_tags: ReportFilter<string[]>;
  has_outstanding_insurer_balance?: ReportFilter<boolean>;
  has_outstanding_patient_balance?: ReportFilter<boolean>;
  managed_care_plan?: ReportFilter<string[]>;
  insurer?: Insurer[] | null;
};

export const ACCOUNT_RECEIVABLE_DEFAULT_FILTERS: AccountReceivableCustomFiltersValues =
  {
    clinic: null,
    sale_age: null,
    patient: null,
    provider: null,
    payment_source_type: null,
    assigned_tags: null,
    has_outstanding_insurer_balance: null,
    has_outstanding_patient_balance: null,
    managed_care_plan: null,
    insurer: null,
  };

type UseInvoicesReceivableCustomFilterOptionsProps = {
  updateCustomFilter: (
    filterName: keyof SalesFilters,
    filterValue: SalesFilters[keyof SalesFilters] | undefined | null
  ) => void;
  selectedCustomFilters: AccountReceivableCustomFiltersValues;
  setSelectedCustomFilters: React.Dispatch<
    React.SetStateAction<AccountReceivableCustomFiltersValues>
  >;
};

export default function useInvoicesReceivableCustomFilterOptions({
  updateCustomFilter,
  selectedCustomFilters,
  setSelectedCustomFilters,
}: UseInvoicesReceivableCustomFilterOptionsProps) {
  const hasRegions = useAppSelector(selectHasRegions);

  const filterOptions: FilterOption<
    AccountReceivableCustomFiltersValues,
    SalesFilters
  >[] = useMemo(() => {
    return [
      ...(hasRegions
        ? [
            {
              name: "region",
              label: "Region",
              renderViewMode: (filterKey, metadata) => (
                <span>
                  By Region:{" "}
                  <b>
                    {metadata?.length > 1 ? (
                      <>{metadata?.length + " Selected"}</>
                    ) : (
                      metadata?.map((item: { label: string }) => item?.label)
                    )}
                  </b>
                </span>
              ),
              onDelete: () => {
                setSelectedCustomFilters((prev) => ({ ...prev, clinic: null }));
              },
              editComponent: (
                filterKey,
                setMetadata,
                control,
                updateFilter
              ) => (
                <SelectRegion
                  name="region"
                  control={control}
                  multiple
                  onOptionSelected={(option) => {
                    updateFilter(
                      Array.isArray(option)
                        ? option
                            ?.map((item: { value: string }) => item?.value)
                            .join(",")
                        : option?.value
                    );
                    setMetadata(option);
                  }}
                />
              ),
            } as FilterOption<
              AccountReceivableCustomFiltersValues,
              SalesFilters
            >,
          ]
        : []),
      {
        name: "clinic",
        label: "Clinic",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Clinic:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectClinic
            name="clinic"
            showAllRegions
            skipFilteringByUser
            control={control}
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            filterByRegions={[]}
          />
        ),
      },
      {
        name: "sale_age",
        label: "Aging Group",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Aging Group:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectAgingGroup
            name="sale_age"
            control={control}
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
          />
        ),
      },
      {
        name: "patient",
        label: "Patient",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Patient: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectPatient
            name="patient"
            control={control}
            onOptionSelected={(selected) => {
              if (selected) {
                updateFilter(selected.value);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            showInactive
          />
        ),
      },
      {
        name: "provider",
        label: "Staff",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Staff:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectProvider
            name="provider"
            control={control}
            label="By Staff"
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
          />
        ),
      },

      {
        name: "payment_source_type",
        label: "Payment Source ",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Payment Source: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectPaymentSourceType
            name="payment_source_type"
            control={control}
            onOptionSelected={(selected) => {
              if (selected) {
                updateFilter(selected.value);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
          />
        ),
      },
      {
        name: "assigned_tags",
        label: "Tag",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Tag:{" "}
            <b>
              {metadata?.length > 1 ? (
                <>{metadata?.length + " Selected"}</>
              ) : (
                metadata?.map((item: { label: string }) => item?.label)
              )}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control) => (
          <SelectTag
            name="assigned_tags"
            tagType={TagTypes.SALE}
            control={control}
            onOptionSelected={(option) => {
              setMetadata(option);
            }}
          />
        ),
      },
      {
        name: "has_outstanding_insurer_balance",
        label: "Has Outstanding Insurer Balance",
        renderViewMode: (filterKey, metadata) => (
          <span>
            Has Outstanding Insurer Balance:{" "}
            <b>{Boolean(metadata.value) ? "Yes" : "No"}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectBooleanValue
            name="has_outstanding_insurer_balance"
            control={control}
            onOptionSelected={(option) => {
              updateFilter(option?.value ?? null);

              setMetadata(option);
            }}
            label="Has Outstanding Insurer Balance"
          />
        ),
      },
      {
        name: "has_outstanding_patient_balance",
        label: "Has Outstanding Patient Balance",
        renderViewMode: (filterKey, metadata) => (
          <span>
            Has Outstanding Patient Balance:{" "}
            <b>{Boolean(metadata.value) ? "Yes" : "No"}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectBooleanValue
            name="has_outstanding_patient_balance"
            control={control}
            onOptionSelected={(option) => {
              updateFilter(option?.value ?? null);

              setMetadata(option);
            }}
            label="Has Outstanding Patient Balance"
          />
        ),
      },
      {
        name: "insurer",
        label: "Insurer",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Insurer:
            <b>
              {metadata?.length === 1
                ? `${metadata[0].name}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectInsurer
            name="insurer"
            control={control}
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.id).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
          />
        ),
      },
      {
        name: "managed_care_plan",
        label: "Managed Care Plan",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Managed Care Plan:
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectManagedCarePlan
            name="managed_care_plan"
            control={control}
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
          />
        ),
      },
    ];
  }, [hasRegions, setSelectedCustomFilters]);

  const filters = useCustomFilters<
    AccountReceivableCustomFiltersValues,
    SalesFilters
  >({
    updateCustomFilter,
    filterOptions,
    selectedCustomFilters,
    setSelectedCustomFilters,
  });

  return filters;
}
